import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import Login from "./components/Auth/Login";

import Dashboard from "./components/Dashboard/Dashboard";
import SettingsPage from "./components/Common/SettingsPage";
import Logs from "./components/Common/Logs";
import Profile from "./components/Common/Profile";
import Orchestrator from "./components/Common/Orchestrator";
import EmailTrigger from "./components/Triggers/EmailTrigger"; //  Import EmailTrigger
import FilePathTrigger from "./components/Triggers/FilePathTrigger"; //  Import FilePathTrigger
import LinkedTaskTrigger from "./components/Triggers/LinkedTaskTrigger"; //  Import LinkedTaskTrigger
import EditEmailTrigger from './components/Triggers/EditEmailTrigger';
import EditFilePathTrigger from './components/Triggers/EditFilePathTrigger';
import EditLinkedTaskTrigger from './components/Triggers/EditLinkedTaskTrigger';
import { AuthProvider } from "./context/AuthContext";
import { TenantProvider } from "./context/TenantContext";  //  Import TenantProvider
import PrivateRoute from "./components/Common/PrivateRoute";
import Layout from "./components/Common/Layout";
import Sidebar from "./components/Common/MainSidebar";
import LoggingService from "./services/LoggingService";
import TriggerMonitoring from "./components/Triggers/TriggerMonitoring"; // Import Trigger Monitoring
import PremiseOrCloud from "./components/Auth/PremiseOrCloud";
import Authorization from "./components/Auth/Authorization";
import JobAnalytics from "./components/Dashboard/JobAnalytics";
import Reports from "./components/Common/Reports";
import { ToastContainer } from 'react-toastify';
import WhatsAppTrigger from './components/Triggers/WhatsAppTrigger';
import EditWhatsAppTrigger from './components/Triggers/EditWhatsAppTrigger';
import setupAxiosInterceptors from "./services/axiosSetup";


const LogNavigation = () => {
  const location = useLocation();

  useEffect(() => {
    LoggingService.log(`User navigated to ${location.pathname}`);
  }, [location]);

  return null;
};

// Add the verification function
async function verifyLoggingSystem() {
  const results = await LoggingService.verifyLoggingSetup();
  console.table(results);

  if (!Object.values(results).every(result => result)) {
    console.warn('Some logging features may not work properly:', results);
  }
}



const App = () => {
  // Add useEffect to run verification when app starts
  useEffect(() => {
    verifyLoggingSystem();
    setupAxiosInterceptors(); // Initialize interceptors
  }, []);

  return (
    <AuthProvider>
      <TenantProvider>
        <Router>
          <LogNavigation />
          <Routes>
            {/* Public Routes (No Sidebar) */}
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<Login />} />


            {/* Protected Routes (With Sidebar) */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />  {/* Sidebar only appears inside Layout */}
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/premise-or-cloud"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <PremiseOrCloud />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/authorization"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <Authorization />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <SettingsPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <Logs />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <Profile />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/orchestrator"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <Orchestrator />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/email-trigger"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <EmailTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/file-path-trigger"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <FilePathTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/linked-task-trigger"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <LinkedTaskTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-email-trigger/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <EditEmailTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-file-path-trigger/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <EditFilePathTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-linked-task-trigger/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <EditLinkedTaskTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/trigger-monitoring"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <TriggerMonitoring />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <JobAnalytics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <Reports />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Add WhatsApp Trigger Route */}
            <Route
              path="/edit-whatsapp-trigger/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <EditWhatsAppTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/whatsapp-trigger"
              element={
                <PrivateRoute>
                  <Layout>
                    <Sidebar />
                    <WhatsAppTrigger />
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Router>
      </TenantProvider>
    </AuthProvider>

  );
};


export default App;
